import React, { useRef } from 'react';
import { KMMError } from '@norlys-a-s/norlys-play-kmm';
import { useAtom, useSetAtom } from 'jotai';
import { setFocus } from '@noriginmedia/norigin-spatial-navigation';
import { Button, Props } from '@smart-tv/components/error-dialog/error-dialog';
import { errorAtom, isErrorDialogOpenAtom, playerFocusKeyAtom } from '../../atoms';
import { playerControlsOpenAtom } from '@smart-tv/components/player/player-content/use-player-content';
import useAuthentication from '@smart-tv/hooks/use-authentication/use-authentication';
import { loginEmailInputFieldFocusKey } from '@smart-tv/e2e/shared/variables';

export class BaseError {
  info: string;
  offline?: boolean;
  constructor(info: string, offline?: boolean) {
    this.info = info;
    this.offline = offline;
  }
  convertErrorToErrorProps = (primaryButton?: Button, secondaryButton?: Button): Props => {
    return {
      title: errorTitle,
      message: this.info,
      primaryButton,
      secondaryButton
    };
  };
}
export class CmdError extends BaseError {
  code: number;
  constructor(code: number, info: string, offline?: boolean) {
    super(info + ' [' + code + ']', offline);
    this.code = code;
  }
}

export const errorTitle = 'Noget gik galt';

export const tryAgainLabel = 'Prøv igen';

export const cancelLabel = 'Annuller';

export const networkErrorMessage = 'Tjek din internetforbindelse og prøv igen.';

export const networkError = new BaseError(networkErrorMessage, true);

export const unknownCMDError = new BaseError('Der skete en uventet fejl. Prøv igen.', false);

export type ErrorCodes = {
  LOGIN_FAILED: number;
  USER_NOT_ACTIVATED: number;
  MISSING_SPACE: number;
  MAX_DEVICES: number;
  MISSING_FAVORITES_LIST: number;
  PLAYER_MISSING_FAVORITES_LIST: number;
  MISSING_CONTENT_INFO_FOR_ASSET: number;
};

export const ERROR_CODES: ErrorCodes = {
  LOGIN_FAILED: 1010075,
  USER_NOT_ACTIVATED: 1110217,
  MISSING_SPACE: 1110174,
  MAX_DEVICES: 1060359,
  MISSING_FAVORITES_LIST: 1060427,
  PLAYER_MISSING_FAVORITES_LIST: 1110226,
  MISSING_CONTENT_INFO_FOR_ASSET: 1030448
};

export class VoPlayerError extends BaseError {
  code: string;
  constructor(code: string) {
    super('Der opstod et problem med at afspille indholdet.' + ' [' + code + ']', false);
    this.code = code;
  }
}

export interface ErrorContextProps {
  children: React.ReactNode | React.ReactNode[];
}

export interface ErrorContextValues {
  errorOpen: boolean;
  errorProps: IErrorDialogProps | null;
}

export interface IErrorDialogProps {
  title: string;
  message: string;
  primaryButton: Button;
  secondaryButton?: Button;
  kmmError?: boolean;
}

class ErrorDialogProps implements IErrorDialogProps {
  title: string;
  message: string;
  primaryButton: Button;
  secondaryButton?: Button;
  kmmError?: boolean;

  constructor(
    title: string,
    message: string,
    primaryButtonText: string,
    primaryButtonOnClick: () => void,
    kmmError?: boolean,
    secondaryButtonText?: string,
    secondaryButtonOnClick?: () => void
  ) {
    this.title = title;
    this.message = message;
    this.primaryButton = {
      label: primaryButtonText,
      onClick: primaryButtonOnClick
    };
    this.secondaryButton =
      secondaryButtonText && secondaryButtonOnClick
        ? {
            label: secondaryButtonText,
            onClick: secondaryButtonOnClick
          }
        : undefined;
    if (kmmError) {
      this.kmmError = kmmError;
    }
  }
}

export const ErrorContext = React.createContext({} as ErrorContextValues);

export const ErrorProvider = (props: ErrorContextProps) => {
  const { children } = props;

  const [errorDic, setErrorDic] = useAtom(errorAtom);
  const [errorOpen, setErrorOpen] = useAtom(isErrorDialogOpenAtom);
  const setPlayerFocusKeyAtom = useSetAtom(playerFocusKeyAtom);
  const setOpenControls = useSetAtom(playerControlsOpenAtom);

  const focusKeyPrev = useRef<string | null>(null);
  const [errorProps, setErrorProps] = React.useState<IErrorDialogProps | null>(null);

  const { removeAuthentication } = useAuthentication();

  const closeErrorDialog = React.useCallback(() => {
    setErrorDic(null);
    setErrorOpen(false);
    setErrorProps(null);
  }, [setErrorOpen, setErrorDic, setErrorProps]);

  const handleClose = React.useCallback(() => {
    if (errorDic?.isStreamPlaying) {
      const focusKey = errorDic?.playerFocusKey;
      setPlayerFocusKeyAtom(focusKey ?? '');
    } else {
      focusKeyPrev.current && setFocus(focusKeyPrev.current);
    }
    closeErrorDialog();
  }, [errorDic, setPlayerFocusKeyAtom, closeErrorDialog]);

  const handleLogOut = React.useCallback(() => {
    setFocus(loginEmailInputFieldFocusKey);
    closeErrorDialog();
  }, [closeErrorDialog]);

  // Atom state automatically synced

  React.useEffect(() => {
    if (!errorDic) return;
    const error = errorDic?.err;

    let errorProps: IErrorDialogProps | null = null;
    if (
      error === networkError ||
      (error as BaseError).offline ||
      error instanceof KMMError.NoInternetError ||
      error instanceof VoPlayerError
    ) {
      errorProps = null;
      setErrorDic(null);
    } else if (error instanceof KMMError) {
      switch (error.constructor) {
        case KMMError.ApiError:
          errorProps = new ErrorDialogProps(
            errorTitle,
            error.message + '[' + (error as KMMError.ApiError).code + ']',
            'OK',
            handleClose,
            true
          );
          break;
        case KMMError.NoTokenError:
          removeAuthentication();
          errorProps = new ErrorDialogProps(
            errorTitle,
            'Du er desværre blevet logget ud. Prøv at logge ind igen.',
            'OK',
            handleLogOut,
            true
          );
          break;
        case KMMError.TokenDuplicationError:
        case KMMError.IllegalStateError:
        case KMMError.IndexOutOfBoundsError:
        case KMMError.InvalidArgumentError:
          errorProps = new ErrorDialogProps(
            errorTitle,
            'Der skete en fejl under kommunikationen med vores systemer.',
            'OK',
            handleClose,
            true
          );
          break;
        default:
          errorProps = new ErrorDialogProps(
            errorTitle,
            'Der skete en ukendt fejl, prøv igen.',
            'OK',
            handleClose,
            true
          );
      }
    } else {
      errorProps = new ErrorDialogProps(
        errorTitle,
        error?.info ? error.info : 'Der skete en ukendt fejl, prøv igen.',
        'OK',
        handleClose
      );
    }

    focusKeyPrev.current = errorDic?.prevFocusKey ?? '';
    setErrorProps(errorProps);
    setOpenControls(false);
  }, [setErrorOpen, errorDic, handleClose]);

  const contextValue = React.useMemo(
    () => ({
      errorOpen,
      errorProps
    }),
    [errorOpen, errorProps]
  );

  return <ErrorContext.Provider value={contextValue}>{children}</ErrorContext.Provider>;
};

export default ErrorContext;
